import React, { useState } from 'react';
import { Send, Mail } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useToast } from '@/hooks/use-toast';
import axios from 'axios';
import { contactInfo } from '@/mock';
import AddressAutocomplete from './AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    passagers: '1',
    depart: '',
    arrivee: '',
    date: '',
    heure: '09',
    minute: '00',
    message: '',
    kilometres: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [departCoords, setDepartCoords] = useState(null);
  const [arriveeCoords, setArriveeCoords] = useState(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);

  // Calculer la distance par la route via l'API Géoportail
  const calculateRouteDistance = async (startCoords, endCoords) => {
    setIsCalculatingDistance(true);
    
    try {
      // Format: longitude,latitude
      const start = `${startCoords.x},${startCoords.y}`;
      const end = `${endCoords.x},${endCoords.y}`;
      
      const url = `https://data.geopf.fr/navigation/itineraire?resource=bdtopo-osrm&start=${start}&end=${end}&profile=car&optimization=fastest`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data && data.geometry && data.geometry.coordinates) {
        // La distance est en mètres, on convertit en km arrondi à la centaine de mètres (0.1 km)
        const distanceMeters = data.distance || 0;
        const distanceKm = Math.round(distanceMeters / 100) / 10;
        setFormData(prev => ({ ...prev, kilometres: distanceKm.toFixed(1) }));
      } else {
        console.error('Erreur: structure de réponse inattendue', data);
        setFormData(prev => ({ ...prev, kilometres: '' }));
        toast({
          title: "Erreur de calcul",
          description: "Impossible de calculer la distance par la route.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Erreur lors du calcul de la distance:', error);
      setFormData(prev => ({ ...prev, kilometres: '' }));
      toast({
        title: "Erreur de calcul",
        description: "Impossible de calculer la distance par la route.",
        variant: "destructive",
      });
    } finally {
      setIsCalculatingDistance(false);
    }
  };

  // Calculer la distance quand les deux adresses sont sélectionnées
  React.useEffect(() => {
    if (departCoords && arriveeCoords) {
      calculateRouteDistance(departCoords, arriveeCoords);
    } else {
      setFormData(prev => ({ ...prev, kilometres: '' }));
    }
  }, [departCoords, arriveeCoords]);

  // Générer les heures de 00 à 23
  const heures = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  
  // Minutes par tranches de 15
  const minutes = ['00', '15', '30', '45'];
  
  // Nombre de passagers de 1 à 4
  const passagers = ['1', '2', '3', '4'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quote-request`, {
        nom: formData.nom,
        telephone: formData.telephone,
        email: formData.email,
        passagers: formData.passagers,
        depart: formData.depart || null,
        arrivee: formData.arrivee || null,
        date: formData.date || null,
        heure: formData.heure && formData.minute ? `${formData.heure}:${formData.minute}` : null,
        kilometres: formData.kilometres || null,
        message: formData.message || null
      });

      toast({
        title: "✅ Demande envoyée !",
        description: "Votre demande de devis a bien été envoyée",
        duration: 3000, // 3 secondes
      });
      
      setIsSubmitting(false);
      setFormData({
        nom: '',
        telephone: '',
        email: '',
        passagers: '1',
        depart: '',
        arrivee: '',
        date: '',
        heure: '09',
        minute: '00',
        kilometres: '',
        message: ''
      });
      setDepartCoords(null);
      setArriveeCoords(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur s'est produite. Veuillez réessayer.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppSend = () => {
    // Validation basique
    if (!formData.nom || !formData.telephone || !formData.email || !formData.depart || !formData.arrivee || !formData.date || !formData.heure) {
      toast({
        title: "Champs requis",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive",
      });
      return;
    }

    // Formater le message WhatsApp
    const heureComplete = `${formData.heure}:${formData.minute || '00'}`;
    const message = `🚗 *Demande de Devis LadyGo VTC*

📋 *Informations client :*
Nom : ${formData.nom}
Téléphone : ${formData.telephone}
Email : ${formData.email}

🗓️ *Détails du trajet :*
Départ : ${formData.depart}
Arrivée : ${formData.arrivee}
Date : ${formData.date}
Heure : ${heureComplete}
Passagers : ${formData.passagers}
${formData.kilometres ? `Distance : ${formData.kilometres} km` : ''}

${formData.message ? `💬 Message :\n${formData.message}` : ''}

Merci de me faire un devis ! 😊`;

    // Encoder le message pour l'URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = contactInfo.whatsapp; // Format: 33611709973
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Ouvrir WhatsApp dans un nouvel onglet
    window.open(whatsappUrl, '_blank');
    
    // Afficher la notification de succès
    toast({
      title: "✅ Demande envoyée !",
      description: "Votre demande de devis a bien été envoyée",
      duration: 3000, // 3 secondes
    });
  };

  return (
    <div className="p-8 rounded-2xl bg-gradient-to-br from-rose-900/25 via-rose-800/15 to-rose-900/10 border border-rose-900/30">
      <h2 className="text-3xl font-light text-white mb-2">Demande de devis</h2>
      <p className="text-gray-400 mb-8">Tous les champs sont obligatoires.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="nom" className="block text-rose-200 text-sm mb-2 tracking-wider">
              NOM *
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all"
              placeholder="Votre nom"
            />
          </div>

          <div>
            <label htmlFor="telephone" className="block text-rose-200 text-sm mb-2 tracking-wider">
              TÉLÉPHONE *
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all"
              placeholder="06 00 00 00 00"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block text-rose-200 text-sm mb-2 tracking-wider">
              EMAIL *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all"
              placeholder="votre@email.com"
            />
          </div>

          <div>
            <label htmlFor="passagers" className="block text-rose-200 text-sm mb-2 tracking-wider">
              NOMBRE DE PASSAGERS *
            </label>
            <select
              id="passagers"
              name="passagers"
              value={formData.passagers}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all"
            >
              {passagers.map((nb) => (
                <option key={nb} value={nb}>
                  {nb} {nb === '1' ? 'passager' : 'passagers'}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="depart" className="block text-rose-200 text-sm mb-2 tracking-wider">
              ADRESSE DÉPART *
            </label>
            <AddressAutocomplete
              id="depart"
              name="depart"
              value={formData.depart}
              onChange={handleChange}
              onSelect={(address, coords) => {
                setFormData({ ...formData, depart: address });
                setDepartCoords(coords);
              }}
              placeholder="Adresse ou aéroport"
              required
            />
          </div>

          <div>
            <label htmlFor="arrivee" className="block text-rose-200 text-sm mb-2 tracking-wider">
              ADRESSE ARRIVÉE *
            </label>
            <AddressAutocomplete
              id="arrivee"
              name="arrivee"
              value={formData.arrivee}
              onChange={handleChange}
              onSelect={(address, coords) => {
                setFormData({ ...formData, arrivee: address });
                setArriveeCoords(coords);
              }}
              placeholder="Adresse ou aéroport"
              required
            />
          </div>
        </div>

        {/* Champ kilomètres (auto-calculé) */}
        <div>
          <label htmlFor="kilometres" className="block text-rose-200 text-sm mb-2 tracking-wider">
            DISTANCE PAR LA ROUTE (KILOMÈTRES)
          </label>
          <div className="relative">
            <input
              type="text"
              id="kilometres"
              name="kilometres"
              value={isCalculatingDistance ? 'Calcul en cours...' : formData.kilometres}
              readOnly
              className="w-full px-4 py-3 rounded-lg bg-black/30 border border-rose-900/30 text-white placeholder-gray-500 cursor-not-allowed transition-all"
              placeholder="Sélectionnez les deux adresses"
            />
            {formData.kilometres && !isCalculatingDistance && (
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-rose-200 text-sm font-medium">
                km
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 italic">
            Distance calculée via les routes (API Géoportail)
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="date" className="block text-rose-200 text-sm mb-2 tracking-wider">
              DATE *
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 border border-rose-900/30 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-transparent transition-all"
              style={{
                colorScheme: 'light'
              }}
            />
          </div>

          <div>
            <label htmlFor="heure" className="block text-rose-200 text-sm mb-2 tracking-wider">
              HEURE *
            </label>
            <select
              id="heure"
              name="heure"
              value={formData.heure}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all"
            >
              {heures.map((h) => (
                <option key={h} value={h}>{h}h</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="minute" className="block text-rose-200 text-sm mb-2 tracking-wider">
              MINUTES *
            </label>
            <select
              id="minute"
              name="minute"
              value={formData.minute}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all"
            >
              {minutes.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-rose-200 text-sm mb-2 tracking-wider">
            MESSAGE
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="4"
            className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all resize-none"
            placeholder="Précisions, bagages, demandes particulières..."
          ></textarea>
        </div>

        {/* Buttons Container */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center justify-center space-x-3 px-8 py-4 rounded-full bg-rose-200 text-gray-900 hover:bg-rose-300 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail className="w-5 h-5" />
            <span className="font-medium">
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer par mail'}
            </span>
          </button>

          {/* WhatsApp Button */}
          <button
            type="button"
            onClick={handleWhatsAppSend}
            className="flex items-center justify-center space-x-3 px-8 py-4 rounded-full bg-green-600 text-white hover:bg-green-700 transition-all duration-300 transform hover:scale-105"
          >
            <FaWhatsapp className="w-5 h-5" />
            <span className="font-medium">Envoyer par WhatsApp</span>
          </button>
        </div>

        <p className="text-center text-gray-400 text-sm">
          Ou appelez directement le{' '}
          <a
            href="tel:+33611709973"
            className="text-rose-200 hover:underline"
          >
            06 11 70 99 73
          </a>
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
